import { getYear } from 'date-fns';
import Decimal from 'decimal.js';
import { compact, first, last, orderBy } from 'lodash';
import * as React from 'react';

import { EditButton } from '@/components/form/baseInputs/Button/EditButton';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { getSaleLoanKindDisplay } from '@/modules/hypotheticalSaleLoans/displayUtils';
import { HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment } from '@/modules/summaryPanels/HypotheticalTransfersSummaryPanel/graphql/HypotheticalTransfersSummary.generated';
import { COLORS } from '@/styles/tokens/colors';
import {
  CharitableTrustPayoutKind,
  CharitableTrustTermKind,
  EstateWaterfallTransferKind,
  HypotheticalInsuranceEndsOnKind,
} from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import {
  GetHypotheticalTransfers_EstateWaterfallLoanFragment,
  GetHypotheticalTransfers_EstateWaterfallTransferFragment,
  GetHypotheticalTransfers_HypotheticalCltFragment,
  GetHypotheticalTransfers_HypotheticalCrtFragment,
  GetHypotheticalTransfers_HypotheticalGratFragment,
  GetHypotheticalTransfers_HypotheticalInsuranceFragment,
  GetHypotheticalTransfers_HypotheticalSaleLoanFragment,
  TransferNode_NodeFragment,
} from './graphql/GetHypotheticalTransfers.generated';
import { OnEditStrategyTransferInput } from './HypotheticalTransfersCard';
import {
  getHypotheticalCltFundingValueFromTransferLog,
  getHypotheticalCrtFundingValueFromTransferLog,
  getHypotheticalGratFundingValueFromTransferLog,
  getSaleLoanFundingValueFromEWLoan,
  getSaleLoanRepaymentValueFromEWLoan,
} from './HypotheticalTransfersCard.utils';
import { TransferTab } from './TransferItems';

export interface SaleLoanTableItem {
  id: string;
  heading: string;
  description: string;
  context: {
    heading: string;
    headingSx?: { color: string };
    subheading: string;
    action: React.ReactNode;
  };
}

type Side = 'source' | 'recipient'; // source is the side that is being loaned from, recipient is the side that is being loaned to

function getDisplayNameFromTransferNode(
  node?: TransferNode_NodeFragment | null
): string | null {
  switch (node?.__typename) {
    case 'Entity':
      return node.subtype?.displayName;
    case 'ClientProfile':
      return node.displayName;
    case 'ClientOrganization':
      return node.name;
    case 'Proposal':
      return node.displayName;
    default:
      return null;
  }
}

export function getSaleLoanSourceOrRecipient(
  side: Side,
  saleLoan: GetHypotheticalTransfers_HypotheticalSaleLoanFragment
): string {
  if (side === 'recipient') {
    switch (saleLoan.recipientKind) {
      case 'ENTITY':
        return (
          saleLoan.recipientEntity?.subtype?.displayName ?? 'Unknown entity'
        );
      case 'INDIVIDUAL':
        return (
          saleLoan.recipientIndividual?.displayName ?? 'Unknown individual'
        );
      default:
        return 'Unknown recipient';
    }
  } else {
    switch (saleLoan.sourceKind) {
      case 'ENTITY':
        return saleLoan.sourceEntity?.subtype?.displayName ?? 'Unknown entity';
      case 'INDIVIDUAL':
        return saleLoan.sourceIndividual?.displayName ?? 'Unknown individual';
      default:
        return 'Unknown source';
    }
  }
}

interface CreateStrategyItemsTableParams {
  /* The heading is which side of the transfer to show as the heading text */
  heading?: Side;
  /* The direction indicates whether the amount in inbound or outbound */
  direction: TransferTab;
}

// createSaleLoanTableFundingItem is to create the table item to show the
// initial sale/loan from the source to the recipient
export function createSaleLoanTableFundingItem(
  saleLoan: GetHypotheticalTransfers_HypotheticalSaleLoanFragment,
  loanProjections: GetHypotheticalTransfers_EstateWaterfallLoanFragment | null,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void
): { item: SaleLoanTableItem; value: Decimal } {
  const value = loanProjections
    ? getSaleLoanFundingValueFromEWLoan(loanProjections)
    : new Decimal(0);

  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );
  const heading = formatCurrencyNoDecimalsAccounting(transferredValue);

  const startYear = saleLoan.startDate.getFullYear();

  const description = (() => {
    if (params.heading === 'source') {
      return `${getSaleLoanKindDisplay(saleLoan.kind, {
        short: true,
      })} from ${getSaleLoanSourceOrRecipient(params.heading, saleLoan)}`;
    } else if (params.heading === 'recipient') {
      return `${getSaleLoanKindDisplay(saleLoan.kind, {
        short: true,
      })} to ${getSaleLoanSourceOrRecipient(params.heading, saleLoan)}`;
    }

    return '';
  })();

  return {
    item: {
      id: saleLoan.id,
      heading: saleLoan.displayName,
      description,
      context: {
        heading,
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: startYear.toString(),
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'saleLoan',
                strategyId: saleLoan.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

// createSaleLoanTableRepaymentItem is to create the table item to show the
// repayment of the sale/loan from the recipient to the source
export function createSaleLoanTableRepaymentItem(
  saleLoan: GetHypotheticalTransfers_HypotheticalSaleLoanFragment,
  loanProjections: GetHypotheticalTransfers_EstateWaterfallLoanFragment | null,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void
): { item: SaleLoanTableItem; value: Decimal } {
  const value = loanProjections
    ? getSaleLoanRepaymentValueFromEWLoan(loanProjections)
    : new Decimal(0);
  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );
  const heading = formatCurrencyNoDecimalsAccounting(transferredValue);
  const startYear = saleLoan.startDate.getFullYear();
  const endYear = startYear + saleLoan.termLengthYears;

  const description = (() => {
    if (!params.heading) {
      return '';
    }

    if (params.direction === TransferTab.Outbound) {
      return `Payments to ${getSaleLoanSourceOrRecipient(params.heading, saleLoan)}`;
    } else {
      return `Payments from ${getSaleLoanSourceOrRecipient(params.heading, saleLoan)}`;
    }
  })();

  return {
    item: {
      id: saleLoan.id,
      heading: saleLoan.displayName,
      description,
      context: {
        heading,
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: `${startYear}-${endYear}`,
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'saleLoan',
                strategyId: saleLoan.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

export function createGratTableItem(
  grat: GetHypotheticalTransfers_HypotheticalGratFragment,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
) {
  const value = getHypotheticalGratFundingValueFromTransferLog(
    grat.simulatedGrat.id,
    transferLog
  );

  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );
  return {
    item: {
      id: grat.id,
      heading: grat.displayName,
      description: `GRAT funding: ${grat.simulatedGrat.subtype.displayName}`,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: `${getYear(new Date())}`,
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'grat',
                strategyId: grat.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

export function createHypotheticalCrtTableItem(
  hypotheticalCrt: GetHypotheticalTransfers_HypotheticalCrtFragment,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
) {
  const value = getHypotheticalCrtFundingValueFromTransferLog(
    hypotheticalCrt.simulatedCrt.id,
    transferLog
  );

  const description = (() => {
    const isTerm =
      hypotheticalCrt.simulatedCrt.crtProposal?.termKind ===
      CharitableTrustTermKind.Fixed;

    if (isTerm) {
      return `Fixed CRT funding: ${hypotheticalCrt.simulatedCrt.displayName}`;
    } else {
      return `Lifetime CRT funding: ${hypotheticalCrt.simulatedCrt.displayName}`;
    }
  })();

  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );
  return {
    item: {
      id: hypotheticalCrt.id,
      heading: hypotheticalCrt.displayName,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: `${getYear(new Date())}`,
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'crt',
                strategyId: hypotheticalCrt.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

export function createHypotheticalCltTableItem(
  hypotheticalClt: GetHypotheticalTransfers_HypotheticalCltFragment,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
) {
  const value = getHypotheticalCltFundingValueFromTransferLog(
    hypotheticalClt.simulatedClt.id,
    transferLog
  );

  const description = (() => {
    const isTerm =
      hypotheticalClt.simulatedClt.cltProposal?.termKind ===
      CharitableTrustTermKind.Fixed;

    if (isTerm) {
      return `Fixed CLT funding: ${hypotheticalClt.simulatedClt.displayName}`;
    } else {
      return `Lifetime CLT funding: ${hypotheticalClt.simulatedClt.displayName}`;
    }
  })();

  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );
  return {
    item: {
      id: hypotheticalClt.id,
      heading: hypotheticalClt.displayName,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: `${getYear(new Date())}`,
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'clt',
                strategyId: hypotheticalClt.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

export function getHypotheticalInsuranceTransferYearsString(
  hypotheticalInsurance: Pick<
    GetHypotheticalTransfers_HypotheticalInsuranceFragment,
    'startYear' | 'endYear' | 'endsOnKind'
  >
): string {
  const { startYear, endYear, endsOnKind } = hypotheticalInsurance;

  switch (endsOnKind) {
    case HypotheticalInsuranceEndsOnKind.Year:
      if (endYear && startYear) {
        return `${startYear}–${endYear}`;
      }

      if (startYear) {
        return startYear.toString();
      }

      return EMPTY_CONTENT_HYPHEN;
    case HypotheticalInsuranceEndsOnKind.PolicyHolderDeath:
      if (startYear) {
        return `${startYear} - Policy holder's death`;
      } else {
        return "Ends on policy holder's death";
      }
    case HypotheticalInsuranceEndsOnKind.TrustTermination:
      if (startYear) {
        return `${startYear} - Trust termination`;
      } else {
        return 'Ends on trust termination';
      }
  }
}

export function createHypotheticalInsuranceTableItem(
  hypotheticalInsurance: GetHypotheticalTransfers_HypotheticalInsuranceFragment,
  params: CreateStrategyItemsTableParams,
  onEditStrategyTransfer: (input: OnEditStrategyTransferInput) => void,
  transferLog: HypotheticalTransfersSummaryQuery_EstateWaterfallTransferFragment[]
) {
  const transfers = transferLog.filter((transfer) => {
    return (
      transfer.kind ===
        EstateWaterfallTransferKind.HypotheticalInsurancePolicyFunding &&
      transfer.associatedObjectID === hypotheticalInsurance.id
    );
  });
  const value = sumDecimalJS(
    compact(transfers.map((transfer) => transfer.finalTransferAmount))
  );

  const sourceName =
    hypotheticalInsurance.sourceEntity?.subtype?.displayName ??
    hypotheticalInsurance.sourceIndividual?.displayName ??
    'Source';
  const destinationName =
    hypotheticalInsurance.destinationEntity?.subtype?.displayName ??
    'Destination';

  const description = `${sourceName} → ${destinationName}`;

  let years: string = getHypotheticalInsuranceTransferYearsString(
    hypotheticalInsurance
  );

  const transferredValue = value.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  return {
    item: {
      id: hypotheticalInsurance.id,
      heading: hypotheticalInsurance.displayName,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading: years,
        action: (
          <EditButton
            onClick={() => {
              onEditStrategyTransfer({
                kind: 'insurance',
                strategyId: hypotheticalInsurance.id,
              });
            }}
          />
        ),
      },
    },
    value: transferredValue,
  };
}

export function createGratAnnuityTableItem(
  transfers: GetHypotheticalTransfers_EstateWaterfallTransferFragment[],
  params: CreateStrategyItemsTableParams
) {
  const orderedTransfers = orderBy(transfers, 'year');

  const { sourceDisplayName, destinationDisplayName, gratDisplayName } =
    (() => {
      const gratEntity = transfers[0]?.source?.node;
      const source = transfers[0]?.source?.node;
      const destination = transfers[0]?.destination?.node;

      let gratDisplayName;

      if (
        gratEntity?.__typename === 'Entity' &&
        gratEntity.subtype?.__typename === 'GRATTrust'
      ) {
        gratDisplayName = gratEntity.subtype?.displayName;
      }

      const sourceDisplayName = getDisplayNameFromTransferNode(source);
      const destinationDisplayName =
        getDisplayNameFromTransferNode(destination);

      return {
        sourceDisplayName,
        destinationDisplayName,
        gratDisplayName,
      };
    })();

  const heading = gratDisplayName || 'GRAT';

  const description =
    (() => {
      if (params.direction === TransferTab.Outbound) {
        return `Annuities to ${destinationDisplayName}`;
      } else {
        return `Annuities from ${sourceDisplayName}`;
      }
    })() ?? '';

  const gratAnnuitiesSum = sumDecimalJS(
    orderedTransfers.map(
      (annuity) => annuity.finalTransferAmount ?? new Decimal(0)
    )
  );

  const firstGratAnnuity = first(orderedTransfers);
  const lastGratAnnuity = last(orderedTransfers);

  const firstGratAnnuityYear = firstGratAnnuity?.year;
  const lastGratAnnuityYear = lastGratAnnuity?.year;

  const transferredValue = gratAnnuitiesSum.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  if (gratAnnuitiesSum.eq(0)) {
    return null;
  }

  return {
    item: {
      id: 'annuities',
      heading,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading:
          firstGratAnnuityYear !== lastGratAnnuityYear
            ? `${firstGratAnnuityYear}-${lastGratAnnuityYear}`
            : `${firstGratAnnuityYear}`,
      },
    },
    value: transferredValue,
  };
}

export function createHypotheticalCharitableTrustIncomePaymentTableItem(
  transfers: GetHypotheticalTransfers_EstateWaterfallTransferFragment[],
  params: CreateStrategyItemsTableParams
) {
  const orderedTransfers = orderBy(transfers, 'year');

  const {
    sourceDisplayName,
    destinationDisplayName,
    charitableTrustDisplayName,
    payoutKind,
  } = (() => {
    const charitableTrust = transfers[0]?.source?.node;
    const source = transfers[0]?.source?.node;
    const destination = transfers[0]?.destination?.node;

    let charitableTrustDisplayName: string | undefined;
    let payoutKind: CharitableTrustPayoutKind | undefined;

    if (
      charitableTrust?.__typename === 'Proposal' &&
      charitableTrust.crtProposal
    ) {
      payoutKind = charitableTrust.crtProposal?.payoutKind;
      charitableTrustDisplayName = charitableTrust.displayName;
    }

    if (
      charitableTrust?.__typename === 'Proposal' &&
      charitableTrust.cltProposal
    ) {
      payoutKind = charitableTrust.cltProposal?.payoutKind;
      charitableTrustDisplayName = charitableTrust.displayName;
    }

    const sourceDisplayName = getDisplayNameFromTransferNode(source);
    const destinationDisplayName = getDisplayNameFromTransferNode(destination);

    return {
      sourceDisplayName,
      destinationDisplayName,
      charitableTrustDisplayName,
      payoutKind,
    };
  })();

  const heading = 'Income bene payments';

  const description =
    (() => {
      if (params.direction === TransferTab.Outbound) {
        return `${payoutKind === CharitableTrustPayoutKind.Annuity ? 'Annuities' : 'Unitrust'} to ${destinationDisplayName}`;
      } else {
        return `${payoutKind === CharitableTrustPayoutKind.Annuity ? 'Annuities' : 'Unitrust'} from ${sourceDisplayName}`;
      }
    })() ?? '';

  const hypotheticalCharitableTrustIncomePaymentsSum = sumDecimalJS(
    orderedTransfers.map(
      (annuity) => annuity.finalTransferAmount ?? new Decimal(0)
    )
  );

  const firstHypotheticalCharitableTrustIncomePayment = first(orderedTransfers);
  const lastHypotheticalCharitableTrustIncomePayment = last(orderedTransfers);

  const firstHypotheticalCharitableTrustIncomePaymentYear =
    firstHypotheticalCharitableTrustIncomePayment?.year;
  const lastHypotheticalCharitableTrustIncomePaymentYear =
    lastHypotheticalCharitableTrustIncomePayment?.year;

  const transferredValue = hypotheticalCharitableTrustIncomePaymentsSum.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  if (hypotheticalCharitableTrustIncomePaymentsSum.eq(0)) {
    return null;
  }

  return {
    item: {
      id: `incomePayments-${destinationDisplayName}`,
      heading: charitableTrustDisplayName || heading, // Use charitable trust display name if available or "Income bene payments"
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading:
          firstHypotheticalCharitableTrustIncomePaymentYear !==
          lastHypotheticalCharitableTrustIncomePaymentYear
            ? `${firstHypotheticalCharitableTrustIncomePaymentYear}-${lastHypotheticalCharitableTrustIncomePaymentYear}`
            : `${firstHypotheticalCharitableTrustIncomePaymentYear}`,
      },
    },
    value: transferredValue,
  };
}

export function createGratBeneficiaryPaymentTableItem(
  transfers: GetHypotheticalTransfers_EstateWaterfallTransferFragment[],
  params: CreateStrategyItemsTableParams
) {
  const orderedPayments = orderBy(transfers, 'year');

  const { sourceDisplayName, destinationDisplayName, gratDisplayName } =
    (() => {
      const gratEntity = transfers[0]?.source?.node;
      const source = transfers[0]?.source?.node;
      const destination = transfers[0]?.destination?.node;

      let gratDisplayName;

      if (
        gratEntity?.__typename === 'Entity' &&
        gratEntity.subtype?.__typename === 'GRATTrust'
      ) {
        gratDisplayName = gratEntity.subtype?.displayName;
      }

      const sourceDisplayName = getDisplayNameFromTransferNode(source);
      const destinationDisplayName =
        getDisplayNameFromTransferNode(destination);

      return {
        sourceDisplayName,
        destinationDisplayName,
        gratDisplayName,
      };
    })();

  const heading = gratDisplayName || 'GRAT';

  const description =
    (() => {
      if (params.direction === TransferTab.Outbound) {
        return `Remainder to ${destinationDisplayName}`;
      } else {
        return `Remainder from ${sourceDisplayName}`;
      }
    })() ?? '';

  const gratBeneficiaryPaymentsSum = sumDecimalJS(
    orderedPayments.map(
      (payment) => payment.finalTransferAmount ?? new Decimal(0)
    )
  );

  const firstGratBeneficiaryPayment = first(orderedPayments);
  const lastGratBeneficiaryPayment = last(orderedPayments);

  const firstGratBeneficiaryPaymentYear = firstGratBeneficiaryPayment?.year;
  const lastGratBeneficiaryPaymentYear = lastGratBeneficiaryPayment?.year;

  const transferredValue = gratBeneficiaryPaymentsSum.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  if (gratBeneficiaryPaymentsSum.eq(0)) {
    return null;
  }

  return {
    item: {
      id: 'beneficiaryPayments',
      heading,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading:
          firstGratBeneficiaryPaymentYear !== lastGratBeneficiaryPaymentYear
            ? `${firstGratBeneficiaryPaymentYear}-${lastGratBeneficiaryPaymentYear}`
            : `${firstGratBeneficiaryPaymentYear}`,
      },
    },
    value: transferredValue,
  };
}

export function createCharitableTrustBeneficiaryPaymentTableItem(
  transfers: GetHypotheticalTransfers_EstateWaterfallTransferFragment[],
  params: CreateStrategyItemsTableParams
) {
  const orderedPayments = orderBy(transfers, 'year');

  const {
    sourceDisplayName,
    destinationDisplayName,
    charitableTrustDisplayName,
  } = (() => {
    const charitableTrust = transfers[0]?.source?.node;
    const source = transfers[0]?.source?.node;
    const destination = transfers[0]?.destination?.node;

    let charitableTrustDisplayName;

    if (charitableTrust?.__typename === 'Proposal') {
      charitableTrustDisplayName = charitableTrust.displayName;
    }

    const sourceDisplayName = getDisplayNameFromTransferNode(source);
    const destinationDisplayName = getDisplayNameFromTransferNode(destination);

    return {
      sourceDisplayName,
      destinationDisplayName,
      charitableTrustDisplayName,
    };
  })();

  // Default heading if we can't get the charitable trust display name
  const defaultHeading = 'Remainder to beneficiary';

  const description =
    (() => {
      if (params.direction === TransferTab.Outbound) {
        return `Remainder to ${destinationDisplayName}`;
      } else {
        return `Remainder from ${sourceDisplayName}`;
      }
    })() ?? '';

  const charitableTrustBeneficiaryPaymentsSum = sumDecimalJS(
    orderedPayments.map(
      (payment) => payment.finalTransferAmount ?? new Decimal(0)
    )
  );

  const firstCharitableTrustBeneficiaryPayment = first(orderedPayments);
  const lastCharitableTrustBeneficiaryPayment = last(orderedPayments);

  const firstCharitableTrustBeneficiaryPaymentYear =
    firstCharitableTrustBeneficiaryPayment?.year;
  const lastCharitableTrustBeneficiaryPaymentYear =
    lastCharitableTrustBeneficiaryPayment?.year;

  const transferredValue = charitableTrustBeneficiaryPaymentsSum.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  if (charitableTrustBeneficiaryPaymentsSum.eq(0)) {
    return null;
  }

  return {
    item: {
      id: 'beneficiaryPayments',
      heading: charitableTrustDisplayName || defaultHeading,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading:
          firstCharitableTrustBeneficiaryPaymentYear !==
          lastCharitableTrustBeneficiaryPaymentYear
            ? `${firstCharitableTrustBeneficiaryPaymentYear}-${lastCharitableTrustBeneficiaryPaymentYear}`
            : `${firstCharitableTrustBeneficiaryPaymentYear}`,
      },
    },
    value: transferredValue,
  };
}

export function createQprtBeneficiaryPaymentTableItem(
  transfers: GetHypotheticalTransfers_EstateWaterfallTransferFragment[],
  params: CreateStrategyItemsTableParams
) {
  const orderedPayments = orderBy(transfers, 'year');

  const { sourceDisplayName, destinationDisplayName, qprtDisplayName } =
    (() => {
      const qprtEntity = transfers[0]?.source?.node;
      const source = transfers[0]?.source?.node;
      const destination = transfers[0]?.destination?.node;

      let qprtDisplayName: string | undefined;

      if (
        qprtEntity?.__typename === 'Entity' &&
        qprtEntity.subtype?.__typename === 'QPRTTrust'
      ) {
        qprtDisplayName = qprtEntity.subtype?.displayName;
      }

      const sourceDisplayName = getDisplayNameFromTransferNode(source);
      const destinationDisplayName =
        getDisplayNameFromTransferNode(destination);

      return {
        sourceDisplayName,
        destinationDisplayName,
        qprtDisplayName,
      };
    })();

  const heading = qprtDisplayName || 'QPRT';

  const description =
    (() => {
      if (params.direction === TransferTab.Outbound) {
        return `Remainder to ${destinationDisplayName}`;
      } else {
        return `Remainder from ${sourceDisplayName}`;
      }
    })() ?? '';

  const qprtBeneficiaryPaymentsSum = sumDecimalJS(
    orderedPayments.map(
      (payment) => payment.finalTransferAmount ?? new Decimal(0)
    )
  );

  const firstQprtBeneficiaryPayment = first(orderedPayments);
  const lastQprtBeneficiaryPayment = last(orderedPayments);

  const firstQprtBeneficiaryPaymentYear = firstQprtBeneficiaryPayment?.year;
  const lastQprtBeneficiaryPaymentYear = lastQprtBeneficiaryPayment?.year;

  const transferredValue = qprtBeneficiaryPaymentsSum.times(
    params.direction === TransferTab.Outbound ? -1 : 1
  );

  if (qprtBeneficiaryPaymentsSum.eq(0)) {
    return null;
  }

  return {
    item: {
      id: 'qprtBeneficiaryPayments',
      heading,
      description,
      context: {
        heading: formatCurrencyNoDecimalsAccounting(transferredValue),
        headingSx: transferredValue.lt(0)
          ? { color: COLORS.MATH.NEGATIVE }
          : undefined,
        subheading:
          firstQprtBeneficiaryPaymentYear !== lastQprtBeneficiaryPaymentYear
            ? `${firstQprtBeneficiaryPaymentYear}-${lastQprtBeneficiaryPaymentYear}`
            : `${firstQprtBeneficiaryPaymentYear}`,
      },
    },
    value: transferredValue,
  };
}
